/**
 * Pub/sub service for the push-permission modal. Same pattern as
 * `expiredLinkModalService.ts` — a module-level singleton + listeners so
 * any code path can open/close the modal without prop-drilling.
 *
 * The modal component reads `state` to render its current variant (chosen
 * at render time from platformService detection helpers, not from state).
 * State here only tracks open/closed and one optional flag for the
 * "we're in the iOS-PWA first-launch trigger path" caller so we know to
 * set the persistent shown-flag on close.
 */
type State = {
  isOpen: boolean;
  // True when opened by PushFirstLaunchTrigger so the close handler can
  // set the one-shot localStorage flag. Other callers (post-register,
  // Settings re-prompt) don't need to gate future re-opens.
  fromFirstLaunch: boolean;
  // Caller-supplied hook invoked AFTER the user grants OS permission AND
  // the FCM token has been PUT to the BE. Used by the Settings push toggle
  // so that the BE-side `notifications.profile.push` pref is only flipped
  // to true once we've confirmed delivery is actually possible — never
  // before, otherwise we'd persist a pref that silently can't deliver.
  onGranted?: () => void | Promise<void>;
};

type Listener = (state: State) => void;

let state: State = { isOpen: false, fromFirstLaunch: false };
const listeners: Set<Listener> = new Set();

/**
 * One-shot localStorage flag: have we already shown the PWA push-permission
 * prompt to this device? Read by `PushFirstLaunchTrigger` to decide whether
 * to surface the modal on first launch; written by `PushPermissionModal`'s
 * close handler when the user dismissed the first-launch variant.
 *
 * Hoisted here (single home, two callers) so the key string isn't duplicated
 * across the trigger + the modal — drift would silently re-prompt every
 * cold boot.
 */
const PWA_PROMPT_SHOWN_FLAG = 'hp_push_pwa_prompt_shown';

export function wasPwaPromptShown(): boolean {
  try {
    return !!localStorage.getItem(PWA_PROMPT_SHOWN_FLAG);
  } catch {
    return false;
  }
}

export function markPwaPromptShown(): void {
  try {
    localStorage.setItem(PWA_PROMPT_SHOWN_FLAG, '1');
  } catch {
    // Safari private mode etc. — best-effort; the prompt may re-fire on
    // next cold boot. Better than crashing the modal close handler.
  }
}

function notify() {
  listeners.forEach((l) => l(state));
}

export function openPushPermissionModal(options?: {
  fromFirstLaunch?: boolean;
  onGranted?: () => void | Promise<void>;
}) {
  state = {
    isOpen: true,
    fromFirstLaunch: options?.fromFirstLaunch ?? false,
    onGranted: options?.onGranted,
  };
  notify();
}

export function closePushPermissionModal() {
  state = { isOpen: false, fromFirstLaunch: false };
  notify();
}

export function getPushPermissionModalState(): State {
  return state;
}

export function subscribeToPushPermissionModal(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}
